import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { AUTH_PATHS } from '@quizdb/common';
import { getWebConfig } from '../../config/loadWebConfig';

export type AccessTokenProvider = () => Promise<string | undefined>;
/** Called on a 401 from a non-auth path; resolves to a fresh token, or undefined if the session is gone. */
export type UnauthorizedHandler = () => Promise<string | undefined>;

// Registered from outside (by `main.tsx`), since this module cannot import the
// session manager without an import cycle.
let accessTokenProvider: AccessTokenProvider | undefined;
let unauthorizedHandler: UnauthorizedHandler | undefined;

export const setAccessTokenProvider = (provider: AccessTokenProvider | undefined): void => {
  accessTokenProvider = provider;
};

export const setUnauthorizedHandler = (handler: UnauthorizedHandler | undefined): void => {
  unauthorizedHandler = handler;
};

const authPaths: readonly string[] = Object.values(AUTH_PATHS);

/**
 * The cookie-based session endpoints (QZ-D29). They never get the stored
 * Bearer token added and a 401 from them never triggers a refresh.
 */
const isAuthPath = (arg: string | FetchArgs): boolean => {
  const url = typeof arg === 'string' ? arg : arg.url;
  return authPaths.includes(url.split('?')[0]);
};

type QuizdbBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

/**
 * Builds a fresh `fetchBaseQuery` per request, reading the API URL from the
 * runtime config (QZ-D10) instead of baking it in as a module-load constant,
 * which would freeze in whatever `getWebConfig()` returned before the config
 * had loaded. `token`, when given, is used instead of asking the provider.
 */
const rawBaseQuery =
  (token?: string): QuizdbBaseQuery =>
  (args, api, extraOptions) =>
    fetchBaseQuery({
      baseUrl: getWebConfig().apiUrl,
      prepareHeaders: async (headers, { arg }) => {
        if (isAuthPath(arg)) {
          return headers;
        }
        const bearer = token ?? (await accessTokenProvider?.());
        if (bearer) {
          headers.set('Authorization', `Bearer ${bearer}`);
        }
        return headers;
      },
    })(args, api, extraOptions);

/**
 * Adds the access token to every non-auth call and, on a 401, asks the
 * registered handler for a fresh token and retries exactly once (QZ-D29).
 */
const baseQuery: QuizdbBaseQuery = async (args, api, extraOptions) => {
  const result = await rawBaseQuery()(args, api, extraOptions);
  if (result.error?.status !== 401 || isAuthPath(args) || !unauthorizedHandler) {
    return result;
  }
  const token = await unauthorizedHandler();
  if (!token) {
    return result;
  }
  return rawBaseQuery(token)(args, api, extraOptions);
};

/**
 * The app's single RTK Query API slice (QZ-D8). Feature endpoint files
 * extend it with `quizdbApi.injectEndpoints`.
 */
export const quizdbApi = createApi({
  reducerPath: 'api',
  tagTypes: [
    'Me',
    'Task',
    'TaskList',
    'TagList',
    'Job',
    'JobList',
    'Spend',
    'SourceList',
    'DocumentList',
    'JobConfigList',
    'JobConfig',
  ],
  baseQuery,
  endpoints: () => ({}),
});
